import { Koi } from '@ksyos/koi';
import type { IAsset } from '../../validation/assets';
import type { ISchemaContainer } from '../ISchemaContainer';
import { assets } from '../../validation/assets';

export interface IDermaOthProcedureCustomControlData {
    condition: {
        anders: string | null;
        jeukt: boolean;
        pijn: boolean;
    };
    conditionBefore: string | null;
    conditionBeforeRemark: string | null;
    conditionChanged: string | null;
    conditionColorChanged: string | null;
    conditionSizeChanged: string | null;
    conditionStarted: string | null;
    detailFiles: IAsset[];
    extraQuestion: string | null;
    overviewFiles: IAsset[];
    treatingCondition: string | null;
    treatingConditionRemark: string | null;
}

export interface IAltoDermaOthProcedureCustomControlData extends IDermaOthProcedureCustomControlData {
    patient: {
        bsn: string | null;
        isSomeoneElse: boolean | null;
        someoneElseName: string | null;
    };
}

export interface IAltoDermaOthProcedureAddValidated {
    customControlData: IAltoDermaOthProcedureCustomControlData;
    procedureId?: string;
}

export const whoAreYouSchema = {
    schema: Koi.object().keys({
        isSomeoneElse: Koi.boolean(),
        someoneElseName: Koi.when('isSomeoneElse', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.string().max(100),
        }),
    }),
};

export const bsnLoginSchema = {
    schema: Koi.object().keys({
        bsn: Koi.string().elfproef(),
    }),
};

export interface IDermaOthProcedureAddValidated {
    customControlData: IDermaOthProcedureCustomControlData;
    procedureId?: string;
}

export const step2RegularSchema = {
    schema: Koi.object().keys({
        overviewFiles: assets.min(1),
    }),
};

export const step3RegularSchema = {
    schema: Koi.object().keys({
        detailFiles: assets.min(1),
    }),
};

export const conditionStartedOptions = [
    'Vandaag/vannacht',
    'Gisteren',
    'Afgelopen dagen',
    'Vorige week',
    'Langer dan een week geleden',
];
export const conditionChangedOptions = ['Verslechterd', 'Verbeterd', 'Gelijk gebleven'];
export const conditionColorChangedOptions = ['Het is roder geworden', 'Het is lichter geworden', 'Gelijk gebleven'];
export const conditionSizeChangedOptions = ['Het is groter geworden', 'Het is kleiner geworden', 'Gelijk gebleven'];
export const yesNoOptions = ['Ja', 'Nee'];

export const step4RegularSchema = {
    schema: Koi.object().keys({
        condition: Koi.object({
            anders: Koi.string().max(550).allow(null),
            jeukt: Koi.boolean(),
            pijn: Koi.boolean(),
        }),
        conditionBefore: Koi.string()
            .valid(...yesNoOptions, 'Weet ik niet zeker')
            .allow(null),
        conditionBeforeRemark: Koi.string().max(550).allow(null),
        conditionChanged: Koi.string()
            .valid(...conditionChangedOptions)
            .allow(null),
        conditionColorChanged: Koi.string()
            .valid(...conditionColorChangedOptions)
            .allow(null),
        conditionSizeChanged: Koi.string()
            .valid(...conditionSizeChangedOptions)
            .allow(null),
        conditionStarted: Koi.string()
            .valid(...conditionStartedOptions)
            .allow(null),
        extraQuestion: Koi.string().max(550).allow(null),
        treatingCondition: Koi.string()
            .valid(...yesNoOptions, 'Weet ik niet')
            .allow(null),
        treatingConditionRemark: Koi.string().max(550).allow(null),
    }),
};

export const dermaOthProcedureSchema: ISchemaContainer<IDermaOthProcedureAddValidated> = {
    schema: Koi.object({
        customControlData: Koi.object()
            .concat(step2RegularSchema.schema)
            .concat(step3RegularSchema.schema)
            .concat(step4RegularSchema.schema),
        procedureId: Koi.string().uuid(),
    }).options({ presence: 'required' }),
};

export const altoDermaOthProcedureSchema: ISchemaContainer<IAltoDermaOthProcedureAddValidated> = {
    schema: Koi.object({
        customControlData: Koi.object({
            patient: Koi.object().concat(whoAreYouSchema.schema).concat(bsnLoginSchema.schema),
        })
            .concat(step2RegularSchema.schema)
            .concat(step3RegularSchema.schema)
            .concat(step4RegularSchema.schema),
        procedureId: Koi.string().uuid(),
    }).options({ presence: 'required' }),
};
